/**
 * 获取 URL 参数
 * @param name 参数名
 * @returns 参数值或null
 */
export const getUrlParam = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(name)
}

/**
 * 移除 URL 中的指定参数
 * @param paramNames 要移除的参数名数组
 */
export const removeUrlParams = (paramNames: string[]): void => {
  const url = new URL(window.location.href)

  paramNames.forEach(param => {
    url.searchParams.delete(param)
  })

  // 更新浏览器历史记录，不刷新页面
  window.history.replaceState({}, '', url.toString())
}

/**
 * 清除认证相关的 URL 参数
 */
export const clearAuthParams = (): void => {
  removeUrlParams(['code', 'state', 'error'])
}

/**
 * 清除所有认证和导航相关的URL参数
 * 包括 code, state, error, from, source 等
 */
export const clearAllAuthParams = (): void => {
  removeUrlParams(['code', 'state', 'error', 'from', 'source', 'redirect'])
}

/**
 * ========= 方案 A（control-tower 登录页回跳 VAS） =========
 * 思路：登录前的 `redirect` 可能会在 `afterEach(clearAllAuthParams)` 被从 URL 清掉，
 * 所以需要先缓存到 sessionStorage，登录成功后再读取并跳转。
 */

export const LOGIN_REDIRECT_STORAGE_KEY = 'ct_login_redirect'

export const cacheLoginRedirect = (redirect: string | undefined) => {
  const value = (redirect || '').trim()
  if (!value) return
  sessionStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, value)
}

export const readCachedLoginRedirect = (): string | undefined => {
  const value = (sessionStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY) || '').trim()
  return value || undefined
}

export const clearCachedLoginRedirect = () => {
  sessionStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY)
}

/**
 * 判断是否是 http(s) 绝对地址
 */
export const isHttpUrl = (value: string): boolean => {
  return /^https?:\/\//i.test(value)
}

/**
 * 外部重定向白名单（host 列表）
 * - 通过 VITE_REDIRECT_ALLOW_HOSTS 配置（逗号分隔，仅 host，不要带 https://）
 */
export const getRedirectAllowHosts = (): string[] => {
  const raw = import.meta.env.VITE_REDIRECT_ALLOW_HOSTS as string | undefined
  const hosts = (raw || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)

  // 默认也允许当前域名
  return Array.from(new Set([window.location.host, ...hosts]))
}

/**
 * 防开放重定向：检查 redirect 是否在允许的 host 列表内
 */
export const isAllowedExternalRedirect = (redirectUrl: string): boolean => {
  try {
    const url = new URL(redirectUrl)
    if (!/^https?:$/i.test(url.protocol)) return false
    return getRedirectAllowHosts().includes(url.host)
  } catch {
    return false
  }
}

/**
 * 注入 token 到目标 URL（给 VAS 的 tracking-query）
 */
export const injectTokenToUrl = (targetUrl: string, token: string): string => {
  if (!token) return targetUrl

  // 如果后续有场景需要 {token} 占位符，这里先兼容
  if (targetUrl.includes('{token}')) {
    return targetUrl.replace('{token}', encodeURIComponent(token))
  }

  try {
    const url = new URL(targetUrl)
    if (!url.searchParams.has('token')) {
      url.searchParams.set('token', token)
    }
    return url.toString()
  } catch {
    return targetUrl
  }
}

/**
 * 登录成功后回跳：
 * - 站内路径：router.push
 * - 外部 http(s)：校验 host 白名单 + 注入 token + window.location.href
 * 返回值表示是否已执行跳转（避免重复 router.push / window.location.href）
 */
export const navigateByRedirect = async(
  router: any,
  redirect: string | undefined,
  tokenForExternal: string | undefined,
  fallbackPath = '/home'
): Promise<boolean> => {
  let raw = (redirect || '').trim()
  if (!raw) raw = readCachedLoginRedirect() || ''
  if (!raw) return false

  // 有些情况下 redirect 可能仍是 URL 编码后的字符串，尝试 decode 一次
  try {
    if (/%[0-9A-Fa-f]{2}/.test(raw)) raw = decodeURIComponent(raw)
  } catch {
    // ignore
  }

  const target = raw.trim()
  if (!target || target === fallbackPath) return false

  if (isHttpUrl(target)) {
    if (!isAllowedExternalRedirect(target)) return false
    const finalUrl = injectTokenToUrl(target, tokenForExternal || '')
    clearCachedLoginRedirect()
    window.location.href = finalUrl
    return true
  }

  // 站内路由一般以 / 开头
  if (target.startsWith('/')) {
    await router.push(target)
    clearCachedLoginRedirect()
    return true
  }

  return false
}

/**
 * 检查URL中是否包含认证参数
 * @returns boolean 是否包含认证参数
 */
export const hasAuthParams = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.has('code') || urlParams.has('state') || urlParams.has('error')
}
